/**
 * @file     oauthProviders.ts
 * @purpose  Client-safe OAuth provider model: which providers exist, which are
 *           switched on, where the provider sends the browser back to, and how
 *           a signed-in user's provider and name are read from the Supabase
 *           user. Shared by the social buttons, /auth/finish and the
 *           oauth-signup route so the rules live in one place.
 */
import { featureFlags } from '@/core/config/featureFlags';
import { safeNextPath } from '@/services/auth/emailLinkOrigin';
import { humanDisplayName, namePartsFromMetadata } from './humanDisplayName';

export type OAuthProvider = 'google' | 'apple';

export const OAUTH_PROVIDERS: readonly OAuthProvider[] = ['google', 'apple'];

export function isOAuthProviderEnabled(provider: OAuthProvider): boolean {
  return provider === 'google' ? featureFlags.authGoogle : featureFlags.authApple;
}

export function enabledOAuthProviders(): OAuthProvider[] {
  return OAUTH_PROVIDERS.filter(isOAuthProviderEnabled);
}

/** Query flag /auth/finish reads to know the return leg came from a provider. */
export const OAUTH_RETURN_PARAM = 'oauth';

/**
 * The provider returns through /auth/finish (implicit flow: tokens arrive in
 * the hash, which only the browser can read). `next` is kept same-origin.
 */
export function oauthRedirectTo(origin: string, provider: OAuthProvider, next: string | null | undefined): string {
  const params = new URLSearchParams({
    next: safeNextPath(next ?? null, '/dashboard'),
    [OAUTH_RETURN_PARAM]: provider,
  });
  return `${origin}/auth/finish?${params.toString()}`;
}

interface UserLike {
  app_metadata?: Record<string, unknown> | null;
  user_metadata?: Record<string, unknown> | null;
  identities?: Array<{ provider?: string | null }> | null;
}

function metadataRecord(value: unknown): Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  return value as Record<string, unknown>;
}

/** The OAuth provider behind this account, or null for email accounts. */
export function oauthProviderOf(user: UserLike | null | undefined): OAuthProvider | null {
  if (!user) return null;
  const primary = user.app_metadata?.provider;
  if (primary === 'google' || primary === 'apple') return primary;
  const listed = user.app_metadata?.providers;
  if (Array.isArray(listed)) {
    const hit = listed.find((p): p is OAuthProvider => p === 'google' || p === 'apple');
    if (hit) return hit;
  }
  const identity = user.identities?.find((i) => i.provider === 'google' || i.provider === 'apple');
  return (identity?.provider as OAuthProvider | undefined) ?? null;
}

/** Name as the provider gave it. Apple shares it only on the first consent. */
export function oauthDisplayName(user: unknown): { name: string | null; firstName?: string; lastName?: string } {
  const meta = user && typeof user === 'object' && 'user_metadata' in user
    ? (user as { user_metadata?: unknown }).user_metadata
    : null;
  const parts = namePartsFromMetadata(metadataRecord(meta));
  const name = humanDisplayName(parts.full, { first: parts.first, last: parts.last });
  if (!name) return { name: null };
  const tokens = name.split(/\s+/);
  return {
    name,
    firstName: parts.first || tokens[0],
    lastName: parts.last || (tokens.length > 1 ? tokens.slice(1).join(' ') : undefined),
  };
}
